import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, useEffect } from 'react';
import { RecentWinners } from '@/components/game/recent-winners';
import { ChampionCallout } from '@/components/leaderboard/champion-callout';
import { RunStanding } from '@/components/leaderboard/run-standing';
import { SayingsPicker } from '@/components/sayings/sayings-picker';
import type { JoinFn } from '@/game/net/connection';
import { useSession } from '@/hooks/use-session';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/play')({
  /**
   * `?room=` is the room the player picked in the lobby, carried here rather than held in a
   * store so that a reload — or a link somebody pasted to a friend — lands in the same room.
   *
   * Anything that is not a string is dropped rather than refused: this is a hint the server is
   * free to overrule, so a mangled URL means "quick play", never an error page. The length bound
   * matches `roomIdSchema`, so a value this route keeps is a value `match.join` will parse.
   */
  validateSearch: (search: Record<string, unknown>): { room?: string; mode?: 'spectate' } => {
    const room =
      typeof search.room === 'string' && search.room.length > 0 && search.room.length <= 64
        ? search.room
        : undefined;
    return {
      room,
      // Watching is only ever a request about a *named* room — there is no quick-play equivalent,
      // because a spectator may not wake or create one (`placeSpectator`). So a `mode=spectate`
      // with no room is dropped to an ordinary join rather than refused: the same treatment a
      // mangled `?room=` gets, and it lands the player in a game either way.
      mode: room !== undefined && search.mode === 'spectate' ? 'spectate' : undefined,
    };
  },
  component: PlayPage,
});

/**
 * Phaser is around a megabyte, and the landing page is the screen this product is judged on, so
 * the entire game module is loaded here and nowhere else. Everything above this line — the
 * landing page, sign-in, the leaderboard — ships without it.
 */
const GameCanvas = lazy(() => import('@/game/GameCanvas'));

/**
 * The game host route: session gate, matchmaking call, canvas.
 *
 * This is the only file that knows both tRPC and the game exist. `match.join` is passed down as
 * a plain function, so the game layer has no opinion about how a room is found and can be
 * tested without a tRPC provider anywhere near it.
 *
 * `?mode=spectate` is the other half: the same route, the same canvas and the same HUD, joined
 * through `match.spectate` instead — which mints a ticket that says "watching" and seats the
 * socket beside the room rather than in it. Leaving spectate mode is a navigation, so the arena
 * is rebuilt around a player's connection rather than a live one being converted.
 *
 * The room id from the lobby rides `?room=` into that call, on every join the connection makes —
 * the first one and every reconnect — so a player who picked a room stays in it. It is a
 * REQUEST: the matchmaker re-decides against live occupancy, and a room that has filled up
 * simply is not where they are seated (`services/match/matchmaker.ts`, step 2). That is why
 * nothing here has to handle "the room I asked for is full": there is no such answer.
 */
function PlayPage() {
  const navigate = useNavigate();
  const { user, isLoading } = useSession();
  const utils = trpc.useUtils();
  const { room, mode } = Route.useSearch();
  const spectating = mode === 'spectate';

  const displayName = user?.displayName ?? '';

  // The vanilla client rather than a mutation hook: joining happens inside the game's connect
  // and reconnect logic, not in a render, and its result is not React state.
  //
  // Two procedures behind one function, which is what keeps the game layer from having an
  // opinion: `match.spectate` answers in the same shape `match.join` does — a room, an address
  // and a signed ticket — and the only difference is the claim inside the ticket. Every reconnect
  // goes through the same call, so a spectator who loses their socket comes back a spectator.
  const join = useCallback<JoinFn>(
    () =>
      spectating && room !== undefined
        ? utils.client.match.spectate.mutate({ roomId: room })
        : utils.client.match.join.mutate({ displayName, roomId: room }),
    [utils, displayName, room, spectating],
  );

  const leave = useCallback(() => {
    navigate({ to: '/' });
  }, [navigate]);

  /**
   * Stop watching and play — the same room, as an ordinary join.
   *
   * A navigation rather than anything the connection does: dropping `mode` re-keys `GameCanvas`
   * below, so the spectator's socket is torn down and a fresh `match.join` opens a player's one.
   * That is the only way round it that cannot leave a half-converted connection behind.
   */
  const joinGame = useCallback(() => {
    navigate({ to: '/play', search: { room } });
  }, [navigate, room]);

  useEffect(() => {
    // No session means no ticket and nothing to join. Back to the landing page, where one click
    // fixes it.
    if (!isLoading && !user) navigate({ to: '/' });
  }, [isLoading, user, navigate]);

  if (isLoading || !user) {
    return <Loading label="Checking your session…" />;
  }

  return (
    <Suspense fallback={<Loading label="Loading the arena…" />}>
      {/*
       * Keyed on the mode: switching between watching and playing is a different socket, a
       * different ticket and a different renderer, so it is a remount rather than a prop change.
       */}
      <GameCanvas
        key={spectating ? 'spectate' : 'play'}
        displayName={displayName}
        join={join}
        onLeave={leave}
        spectate={spectating}
        onJoinGame={spectating ? joinGame : undefined}
        /*
         * The two things in the arena that read the API. They are passed as nodes because the
         * game layer knows no tRPC — this is the only file that knows both exist — and it is also
         * where an overlay show/hide setting would eventually decide not to pass the first one.
         */
        champions={<ChampionCallout variant="overlay" />}
        /*
         * The room's own history, on the screens that WAIT for a round. A slot for `champions`'
         * reason — the arena knows no tRPC — and a query rather than something the room told us,
         * because a room cannot remember across the reaper closing it.
         *
         * `room` is the id the URL asked for. Null for quick play, which draws nothing: this
         * board is about a room a player chose, and the matchmaker may not have seated them in
         * the one they named anyway.
         */
        recentWinners={({ current, className }) => (
          <RecentWinners roomId={room ?? null} current={current} className={className} />
        )}
        standing={<RunStanding />}
        /*
         * The third of them, and the one that also sends: the picker reads the player's own list
         * and the session it is gated on, and hands a saying *id* back through the connection the
         * canvas owns. A render prop rather than a node for exactly that half — see
         * `GameCanvasProps.sayings`.
         */
        sayings={(props) => <SayingsPicker {...props} />}
      />
    </Suspense>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className="arena-grid flex flex-1 items-center justify-center p-4 text-sm text-muted-foreground">
      {label}
    </div>
  );
}
